import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import { AlertTriangle, CreditCard, X, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

interface PaymentRequiredModalProps {
  isOpen: boolean;
  onClose: () => void;
  message?: string;
  feature?: string;
  requiredPlan?: string;
}

const PaymentRequiredModal: React.FC<PaymentRequiredModalProps> = ({
  isOpen,
  onClose,
  message = 'Subscription required',
  feature,
  requiredPlan
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handleUpgradeEvent = (event: CustomEvent) => {
      // This modal will be controlled by the parent component
      // The event is handled by the API service toast notification
    };

    window.addEventListener('subscription:upgrade-required', handleUpgradeEvent as EventListener);
    return () => {
      window.removeEventListener('subscription:upgrade-required', handleUpgradeEvent as EventListener);
    };
  }, []);

  const handleViewPlans = () => {
    navigate('/settings/subscription');
    onClose();
  };

  const handleUpdatePayment = async () => {
    try {
      setLoading(true);
      const response = await apiService.post('/stripe-billing/create-portal-session', {
        returnUrl: window.location.href
      });
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Error creating portal session:', error);
      toast.error('Failed to open payment management');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex items-start mb-4">
          <AlertTriangle className="h-6 w-6 text-orange-500 mr-3 flex-shrink-0 mt-1" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Subscription Required
            </h3>
            <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">
              {message}
            </p>
            {feature && (
              <p className="text-gray-500 dark:text-gray-400 text-xs mb-2">
                Feature: {feature}
              </p>
            )}
            {requiredPlan && (
              <p className="text-gray-500 dark:text-gray-400 text-xs mb-4">
                Required Plan: {requiredPlan}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-3">
          <button
            onClick={handleViewPlans}
            className="w-full bg-accent-color hover:bg-accent-color-600 text-white py-2 px-4 rounded-lg font-medium transition-colors flex items-center justify-center"
          >
            <CreditCard className="h-4 w-4 mr-2" />
            View Plans
          </button>

          {/* Only show "Update Payment" if user has had a subscription before */}
          {user?.companyId && (
            <button
              onClick={handleUpdatePayment}
              disabled={loading}
              className="w-full bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 py-2 px-4 rounded-lg font-medium transition-colors flex items-center justify-center disabled:opacity-50"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CreditCard className="h-4 w-4 mr-2" />
              )}
              Update Payment Method
            </button>
          )}
        </div>

        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
            Need help? Contact support for assistance with your subscription.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentRequiredModal;

